import React, { useState, useEffect, useCallback } from 'react';import apiService from '../../services/api';
import Toast from '../../components/Toast';
import './StockManagement.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { stockHandoverAPI } from '../../services/branchAPI';

function StockManagement() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterSize, setFilterSize] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [handovers, setHandovers] = useState([]);

  const [formData, setFormData] = useState({
    size: '1kg',
    quantity: '',
    notes: '',
    status: 'In Process',
    addedDate: new Date().toISOString().split('T')[0],
  });

  const fetchStocks = useCallback(async () => {
  try {
    setLoading(true);
    const data = await apiService.getAllStocks();
    setStocks(Array.isArray(data) ? data : []);
  } catch (error) {
    showToast('Failed to fetch stocks', 'error');
    console.error(error);
  } finally {
    setLoading(false);
  }
}, []);

// const fetchHandovers = useCallback(async () => {
//   try {
//     const data = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7058/api'}/stockhandover`);
//     const json = await data.json();
//     setHandovers(Array.isArray(json) ? json : []);
//   } catch (error) {
//     console.error('Failed to fetch handovers', error);
//   }
// }, []);



const fetchHandovers = useCallback(async () => {
  try {
    const data = await stockHandoverAPI.getAllHandovers();
    setHandovers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error('Failed to fetch handovers', error);
    showToast('Failed to fetch distribution data', 'error');
  }
}, []);


useEffect(() => {
  fetchStocks();
  fetchHandovers();
}, [fetchStocks, fetchHandovers]);


  const distributedTotals = (() => {
    const totals = { '1kg': 0, '500g': 0, '200g': 0 };

    const filteredHandovers = handovers.filter(h => {
      if (filterSize !== 'all' && h.size !== filterSize) return false;
      if (filterMonth || filterYear) {
        const date = new Date(h.handoverDate);
        if (filterMonth && date.getMonth() + 1 !== parseInt(filterMonth)) return false;
        if (filterYear && date.getFullYear() !== parseInt(filterYear)) return false;
      }
      return true;
    });

    filteredHandovers.forEach(h => {
      if (totals.hasOwnProperty(h.size)) totals[h.size] += h.quantity;
    });
    return totals;
  })();

  // const distributedTotals = calculateDistributedTotals();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.quantity || formData.quantity <= 0) {
      showToast('Quantity must be greater than 0', 'error');
      return;
    }

    const payload = {
      ...formData,
      quantity: parseInt(formData.quantity, 10),
      addedDate: new Date(formData.addedDate).toISOString(),
    };

    try {
      setLoading(true);
      if (editingId) {
        const updated = await apiService.updateStock(editingId, payload);
        // Update just that record in state
        setStocks(prev => prev.map(s => s.id === editingId ? { ...s, ...updated.stock ?? payload, id: editingId } : s));
        showToast('Stock updated successfully', 'success');
      } else {
        const created = await apiService.addStock(payload);
        // Prepend new record to state
        setStocks(prev => [created, ...prev]);
        showToast('Stock added successfully', 'success');
      }

      setFormData({ size: '1kg', quantity: '', notes: '', status: 'In Process', addedDate: new Date().toISOString().split('T')[0] });
      setEditingId(null);
      setShowForm(false);
    } catch (error) {
      showToast(error.message || 'Failed to save stock', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (stock) => {
    setFormData({
      size: stock.size,
      quantity: stock.quantity,
      notes: stock.notes || '',
      status: stock.status,
      addedDate: stock.addedDate ? new Date(stock.addedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0], // 👈 add this
    });
    setEditingId(stock.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this stock record?')) {
      try {
        setLoading(true);
        await apiService.deleteStock(id);
        // Remove just that record from state
        setStocks(prev => prev.filter(s => s.id !== id));
        showToast('Stock deleted successfully', 'success');
      } catch (error) {
        showToast(error.message || 'Failed to delete stock', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ size: '1kg', quantity: '', notes: '', status: 'In Process' });
  };

  const filteredStocks = stocks.filter(stock => {
    if (filterSize !== 'all' && stock.size !== filterSize) return false;
    if (filterMonth || filterYear) {
      const date = new Date(stock.addedDate);
      if (filterMonth && date.getMonth() + 1 !== parseInt(filterMonth)) return false;
      if (filterYear && date.getFullYear() !== parseInt(filterYear)) return false;
    }
    return true;
  });

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

  const getYearOptions = () => {
    const years = [...new Set(stocks.map(s => new Date(s.addedDate).getFullYear()))];
    return years.sort((a, b) => b - a);
  };


  const getFilterLabel = () => {
  const parts = [];
  if (filterSize !== 'all') parts.push(`Size: ${filterSize}`);
  if (filterMonth) parts.push(`Month: ${new Date(2000, parseInt(filterMonth) - 1).toLocaleString('en-US', { month: 'long' })}`);
  if (filterYear) parts.push(`Year: ${filterYear}`);
  return parts.length > 0 ? parts.join(' | ') : 'All Records';
};

const exportToPDF = () => {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(18);
  doc.setTextColor(44, 62, 80);
  doc.text('Stock Management Report', 14, 20);

  // Filter info
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  doc.text(`Filter: ${getFilterLabel()}`, 14, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, 37);

  // Summary section
  doc.setFontSize(12);
  doc.setTextColor(44, 62, 80);
  doc.text('Summary', 14, 48);

  autoTable(doc, {
    startY: 52,
    head: [['Size', 'In Stock', 'Distributed', 'Remaining']],
    body: [
      ['1kg', stockTotals['1kg'], distributedTotals['1kg'], stockTotals['1kg'] - distributedTotals['1kg']],
      ['500g', stockTotals['500g'], distributedTotals['500g'], stockTotals['500g'] - distributedTotals['500g']],
      ['200g', stockTotals['200g'], distributedTotals['200g'], stockTotals['200g'] - distributedTotals['200g']],
    ],
    styles: { fontSize: 11 },
    headStyles: { fillColor: [102, 126, 234] },
    columnStyles: {
      2: { textColor: [231, 76, 60] },
      3: { textColor: [46, 204, 113] },
    },
  });

  // Stock records table
  doc.setFontSize(12);
  doc.setTextColor(44, 62, 80);
  doc.text('Stock Records', 14, doc.lastAutoTable.finalY + 12);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [['ID', 'Size', 'Quantity', 'Added Date', 'Notes']],
    body: filteredStocks.map(stock => [
      stock.id,
      stock.size,
      stock.quantity,
      formatDate(stock.addedDate),
      stock.notes || '-',
    ]),
    styles: { fontSize: 10 },
    headStyles: { fillColor: [102, 126, 234] },
    alternateRowStyles: { fillColor: [248, 249, 250] },
  });

  doc.save(`stock-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

const exportToExcel = () => {
  const wb = XLSX.utils.book_new();

  const summaryData = [
    ['Stock Management Report'],
    [`Filter: ${getFilterLabel()}`],
    [`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`],
    [],
    ['Summary'],
    ['Size', 'In Stock', 'Distributed', 'Remaining'],
    ['1kg', stockTotals['1kg'], distributedTotals['1kg'], stockTotals['1kg'] - distributedTotals['1kg']],
    ['500g', stockTotals['500g'], distributedTotals['500g'], stockTotals['500g'] - distributedTotals['500g']],
    ['200g', stockTotals['200g'], distributedTotals['200g'], stockTotals['200g'] - distributedTotals['200g']],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  const recordsData = [
    ['ID', 'Size', 'Quantity', 'Added Date', 'Notes'],
    ...filteredStocks.map(stock => [
      stock.id,
      stock.size,
      stock.quantity,
      formatDate(stock.addedDate),
      stock.notes || '-',
    ]),
  ];
  const wsRecords = XLSX.utils.aoa_to_sheet(recordsData);
  wsRecords['!cols'] = [{ wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 22 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, wsRecords, 'Stock Records');

  XLSX.writeFile(wb, `stock-report-${new Date().toISOString().split('T')[0]}.xlsx`);
};

  // Calculate total stock for each size
 
  const stockTotals = (() => {
    const totals = { '1kg': 0, '500g': 0, '200g': 0 };
    filteredStocks.forEach(stock => {
      if (totals.hasOwnProperty(stock.size)) totals[stock.size] += stock.quantity;
    });
    return totals;
  })();


  // const stockTotals = calculateStockTotals();

  return (
    <div className="stock-management-container">
      <div className="stock-management-header">
        <h2> Stock Management</h2>
        <button
          className="btn-add-stock"
          onClick={() => setShowForm(true)}
          disabled={loading}
        >
          ➕ Add Stock
        </button>
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="stock-form-container">
          <div className="stock-form-content">
            <h3>{editingId ? 'Edit Stock' : 'Add New Stock'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="size">Size:</label>
                <select id="size" name="size" value={formData.size} onChange={handleInputChange} required>
                  <option value="1kg">1kg</option>
                  <option value="500g">500g</option>
                  <option value="200g">200g</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="quantity">Quantity:</label>
                <input id="quantity" type="number" name="quantity" value={formData.quantity}
                  onChange={handleInputChange} placeholder="Enter quantity" required min="1" />
              </div>

              <div className="form-group">
                <label htmlFor="addedDate">Date:</label>
                <input id="addedDate" type="date" name="addedDate" value={formData.addedDate}
                  onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes (Optional):</label>
                <textarea id="notes" name="notes" value={formData.notes}
                  onChange={handleInputChange} placeholder="Add any notes..." rows="3" />
              </div>

              <div className="form-buttons">
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Saving...' : editingId ? 'Update Stock' : 'Add Stock'}
                </button>
                <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="stock-filter">
        <label htmlFor="filter">Size:</label>
        <select
          id="filter"
          value={filterSize}
          onChange={(e) => setFilterSize(e.target.value)}
        >
          <option value="all">All Sizes</option>
          <option value="1kg">1kg</option>
          <option value="500g">500g</option>
          <option value="200g">200g</option>
        </select>

        <label htmlFor="filterMonth">Month:</label>
        <select
          id="filterMonth"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
        >
          <option value="">All Months</option>
          <option value="1">January</option>
          <option value="2">February</option>
          <option value="3">March</option>
          <option value="4">April</option>
          <option value="5">May</option>
          <option value="6">June</option>
          <option value="7">July</option>
          <option value="8">August</option>
          <option value="9">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>

        <label htmlFor="filterYear">Year:</label>
        <select
          id="filterYear"
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
        >
          <option value="">All Years</option>
          {getYearOptions().map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        {(filterSize !== 'all' || filterMonth || filterYear) && (
          <button
            onClick={() => { setFilterSize('all'); setFilterMonth(''); setFilterYear(''); }}
            style={{
              padding: '8px 12px', borderRadius: '6px', border: '1px solid #bdc3c7',
              background: '#ecf0f1', cursor: 'pointer', fontSize: '13px'
            }}
          >
            ✕ Clear
          </button>
        )}

        <span className="stock-count">Total: {filteredStocks.length} records</span>

        {/* Export Buttons */}
  <div className="export-buttons">
    <button className="btn-export btn-export-pdf" onClick={exportToPDF}>
      📄 Export PDF
    </button>
    <button className="btn-export btn-export-excel" onClick={exportToExcel}>
      📊 Export Excel
    </button>
  </div>
      </div>

      {/* Stock Summary Cards */}
      <div className="stock-summary">
        {[['1kg', 'summary-1kg'], ['500g', 'summary-500g'], ['200g', 'summary-200g']].map(([size, cls]) => (
          <div key={size} className={`summary-card ${cls}`}>
            <div className="summary-icon"></div>
            <div className="summary-content">
              <h3>{size}</h3>
              <p className="summary-value">{stockTotals[size]}</p>
              <span className="summary-label">units in stock</span>
              <div className="summary-distributed">
                <span className="distributed-value">{distributedTotals[size]}</span>
                <span className="summary-label">units distributed</span>
              </div>
              <div className="summary-remaining">
                <span className="remaining-value">{stockTotals[size] - distributedTotals[size]}</span>
                <span className="summary-label">units remaining</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stock Table */}
      {loading && !showForm ? (
        <div className="loading">Loading stocks...</div>
      ) : filteredStocks.length === 0 ? (
        <div className="no-records">No stock records found</div>
      ) : (
        <div className="stock-table-wrapper">
          <table className="stock-table">
            <thead>
  <tr>
    <th>ID</th>
    <th>Size</th>
    <th>Quantity</th>
    <th>Added Date</th>
    <th>Notes</th>
    <th>Actions</th>
  </tr>
</thead>
<tbody>
  {filteredStocks.map(stock => (
    <tr key={stock.id}>
      <td>{stock.id}</td>
      <td>
        <span className={`size-badge size-${stock.size.replace(/\s+/g, '')}`}>
          {stock.size}
        </span>
      </td>
      <td>{stock.quantity}</td>
      <td>{formatDate(stock.addedDate)}</td>
      <td>{stock.notes || '-'}</td>
      <td className="action-buttons">
        <button className="btn-edit" onClick={() => handleEdit(stock)} title="Edit">✏️</button>
        <button className="btn-delete" onClick={() => handleDelete(stock.id)} title="Delete">🗑️</button>
      </td>
    </tr>
  ))}
</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default StockManagement;
